import { Injectable } from "@angular/core";
import { PRIMITIVE, ServiceMessageBrokerFactory } from "shared/provider-webworker";

import { ActiveUserProfilePromiseService } from "shared/auth-services";
import { BranchLookupService } from "./BranchLookupService";
import { PrimaryLookupService } from "../../impl/PrimaryLookupService";
import { IBranchPromiseLookupService } from "../IBranchPromiseLookupService";
import { ActiveUser } from "../../../model/core/ActiveUser";

import { CubiqWorker } from "../../../worker/CubiqWorker";

import * as CubiqTS from "shared/cubiq-model-multi-asset";
import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

const MAX_SUB_BRANCH_DEPTH = 15;

@Injectable()
export class BranchPrimaryLookupService
  extends PrimaryLookupService<string, CubiqTS.BranchImpl>
  implements IBranchPromiseLookupService
{
  private rateSrc: CubiqTS.RateSourceImpl[];
  private tradingChannelProfiles: CubiqTS.TradingChannelProfileImpl[];
  private activeUser: ActiveUser;

  constructor(
    protected base: BranchLookupService,
    protected factory: ServiceMessageBrokerFactory,
    protected cubiqWorker: CubiqWorker,
    protected activeUserService: ActiveUserProfilePromiseService,
  ) {
    super(base, factory, CubiqTS.BranchImpl, cubiqWorker, "BranchImpl");

    cubiqWorker.isAuth(`${this.constructor.name}`).then(
      () => {
        Promise.all([
          new Promise<void>((res, rej) => {
            cubiqWorker.primeStaticData<CubiqTS.BranchImpl>(CubiqTS.BranchImpl.name).subscribe(
              (branch: CubiqTS.BranchImpl[]) => {
                activeUserService.getActiveUserData().then(
                  (activeUser: ActiveUser) => {
                    this.activeUser = activeUser;
                    this.base.setActiveBranchCode(activeUser.branchCd);
                    this.base.prime(branch);
                  },
                  (error) => {
                    rej(error);
                  },
                );
              },
              (error: Error) => {
                console.debug(`${this.constructor.name} ${CubiqTS.BranchImpl.name} prime failed`);
                rej(error);
              },
              () => {
                res();
              },
            );
          }),
          // new Promise<void>((res, rej) => {
          // cubiqWorker
          //     .primeStaticData<string>('VisibleBranchCodes')
          //     .subscribe((branchCode: string[]) => {
          //         this.base.setVisiblePrivateBranchCodes(branchCode);
          //     }, (error: Error) => {
          //         console.debug(`${this.constructor.name} VisiblePrivateBranchCodes prime failed`);
          //         rej(error);
          //     }, () => {
          //         res();
          //     });
          // }),
        ]).then(() => {
          this._primed.resolve(true);
        });
      },
      (reason) => {
        console.debug("BranchPrimaryLookupService: Failed to prime: " + reason);

        this._primed.resolve(false);
      },
    );

    this.broker.registerMethod("getBranch", [PRIMITIVE], this.getBranch.bind(this), this.VALUE);
    this.broker.registerMethod(
      "getAllBranchCodes",
      [],
      this.getAllBranchCodes.bind(this),
      PRIMITIVE,
    );
    this.broker.registerMethod(
      "getCompleteListOfBranchCodes",
      [],
      this.getCompleteListOfBranchCodes.bind(this),
      PRIMITIVE,
    );
  }

  protected processModelUpdateEvent(event: CubiqTS.ModelUpdateImpl): void {
    super.processModelUpdateEvent(event);

    if (
      event == Object(event) &&
      event.payLoad == Object(event.payLoad) &&
      event.payLoad instanceof CubiqTS.BranchImpl
    ) {
      let newBranch = event.payLoad;
      let updated = false;

      if (this.activeUser.isTrader()) {
        if (event.eventType == CubiqTS.ModelUpdateType.DELETE) {
          this.activeUser.tradableBranchCodes.delete(newBranch.branchId);
          updated = true;
        } else if (this.activeUser.entryUnRestricted) {
          this.activeUser.tradableBranchCodes.add(newBranch.branchId);
          updated = true;
        } else if (newBranch != null && newBranch.privateFg == true) {
          if (newBranch.branchId == this.activeUser.branchCd) {
            this.activeUser.tradableBranchCodes.add(newBranch.branchId);
          } else {
            this.activeUser.tradableBranchCodes.delete(newBranch.branchId);
          }
          updated = true;
        } else if (
          newBranch != null &&
          newBranch.privateFg == false &&
          this.isSubBranch(newBranch, this.activeUser.branchCd)
        ) {
          this.activeUser.tradableBranchCodes.add(newBranch.branchId);
          updated = true;
        } else {
          this.activeUser.tradableBranchCodes.delete(newBranch.branchId);
          updated = true;
        }

        if (updated) {
          this.activeUserService.setActiveUserData(this.activeUser);
        }
      }
    }
  }

  /**
   * is branch a same as or within hierarchy of user's current branch cd
   * @param branch
   * @param userBranchCd
   */
  private isSubBranch(branch: CubiqTS.BranchImpl, userBranchCd: string): boolean {
    let maxDepth = MAX_SUB_BRANCH_DEPTH;
    let depth = 0;
    let currentBranch = branch;

    while (
      currentBranch != null &&
      currentBranch instanceof CubiqTS.BranchImpl &&
      depth < maxDepth
    ) {
      if (currentBranch.branchId == userBranchCd) {
        return true;
      }

      if (currentBranch.parentBranchCd == userBranchCd) {
        return true;
      }

      if (currentBranch.parentBranchCd != null) {
        currentBranch = this.base.getItem(currentBranch.parentBranchCd);
      } else {
        currentBranch = null;
      }

      depth++;
    }

    return false;
  }

  getBranch(id: string): Promise<CubiqTS.BranchImpl> {
    return new Promise<CubiqTS.BranchImpl>((res, rej) => {
      res(this.base.getBranch(id));
    });
  }

  getBranchNoPromise(id: string): CubiqTS.BranchImpl {
    return this.base.getBranch(id);
  }

  getAllBranchCodes(): Promise<Array<string>> {
    return new Promise<Array<string>>((res, rej) => {
      res(this.base.getAllBranchCodes());
    });
  }

  getVisiblePrivateBranchCodes(): Promise<Array<string>> {
    return new Promise<Array<string>>((res, rej) => {
      res(Array.from(this.base.getVisiblePrivateBranchCodes()));
    });
  }

  getCompleteListOfBranchCodes(): Promise<Array<string>> {
    return new Promise<Array<string>>((res, rej) => {
      res(this.base.getCompleteListOfBranchCodes());
    });
  }
}
